import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn';
import styles from './Dropdown.module.css';

export type DropdownItem = {
  readonly id: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly danger?: boolean;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
};

export type DropdownProps = {
  readonly trigger: (props: { readonly isOpen: boolean; readonly toggle: () => void }) => ReactNode;
  readonly items: readonly DropdownItem[];
  readonly align?: 'start' | 'end';
  readonly header?: ReactNode;
};

/** Позиционирование и клавиатура (docs/06 §4). */
export function Dropdown({ trigger, items, align = 'start', header }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const toggle = (): void => {
    const node = anchorRef.current;
    if (!node) {
      return;
    }
    const rect = node.getBoundingClientRect();
    setCoords({ x: align === 'end' ? rect.right : rect.left, y: rect.bottom });
    setIsOpen((open) => !open);
  };

  return (
    <div className={styles['anchor']} ref={anchorRef}>
      {trigger({ isOpen, toggle })}
      {isOpen && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className={cn(styles['menu'], align === 'end' && styles['alignEnd'])}
              style={{ left: coords.x, top: coords.y + 4 }}
            >
              {header ? <div className={styles['header']}>{header}</div> : null}
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  className={cn(styles['item'], item.danger && styles['danger'])}
                  onClick={() => {
                    setIsOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
